import React, { useState } from 'react';
import { ChevronDown, Plus, Settings, Users, Building2 } from 'lucide-react';
import { useTeams } from '../hooks/useTeams';
import type { TeamWithMembers } from '../types';

interface TeamSelectorProps {
  selectedTeamId: string | null;
  onTeamSelect: (teamId: string | null) => void;
  onCreateTeam: () => void;
  onJoinTeam: () => void;
  onTeamSettings?: (team: TeamWithMembers) => void;
}

export const TeamSelector: React.FC<TeamSelectorProps> = ({
  selectedTeamId,
  onTeamSelect,
  onCreateTeam,
  onJoinTeam,
  onTeamSettings
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { teams, isLoading } = useTeams();

  const selectedTeam = teams.find(team => team.id === selectedTeamId);

  const handleTeamSelect = (teamId: string | null) => {
    onTeamSelect(teamId);
    setIsOpen(false);
  };

  const handleTeamSettings = (team: TeamWithMembers, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTeamSettings) {
      onTeamSettings(team);
    }
    setIsOpen(false);
  };

  const getTeamInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getTeamColor = (teamId: string | null) => {
    if (!teamId) return 'from-indigo-500 to-purple-600';
    
    const colors = [
      'from-blue-500 to-cyan-600',
      'from-emerald-500 to-teal-600', 
      'from-orange-500 to-red-600',
      'from-purple-500 to-pink-600',
      'from-yellow-500 to-orange-600',
      'from-rose-500 to-pink-600',
    ];
    
    const hash = teamId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="relative">
      {/* Main Team Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group flex items-center gap-3 px-4 py-2 rounded-lg border transition-all duration-200 min-w-[160px] justify-between shadow-sm hover:shadow-md ${
          isOpen 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
        }`}
        disabled={isLoading}
      >
        <div className="flex items-center gap-2">
          {/* Team Avatar */}
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getTeamColor(selectedTeamId)} flex items-center justify-center text-white shadow-sm font-semibold text-sm`}>
            {selectedTeam ? getTeamInitials(selectedTeam.name) : '👤'}
          </div>
          
          {/* Team Info */}
          <div className="text-left">
            <div className="font-medium text-gray-900 text-sm leading-tight">
              {selectedTeam ? selectedTeam.name : 'Personal'}
            </div>
          </div>
        </div>
        
        {/* Chevron */}
        <ChevronDown 
          className={`w-4 h-4 text-gray-500 transition-all duration-200 group-hover:text-blue-600 ${
            isOpen ? 'rotate-180 text-blue-600' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute top-full left-0 mt-3 w-[420px] bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden backdrop-blur-sm">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Switch Workspace
              </h3>
              <p className="text-sm text-gray-600 mt-1">Choose your workspace or team</p>
            </div>

            <div className="py-2 max-h-80 overflow-y-auto">
              {/* Personal Option */}
              <button
                onClick={() => handleTeamSelect(null)}
                className={`w-full px-6 py-4 text-left hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 flex items-center gap-4 transition-all duration-200 group ${
                  selectedTeamId === null ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-r-4 border-blue-500' : 'text-gray-700'
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md font-bold text-lg">
                  👤
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 group-hover:text-blue-700">Personal Workspace</div>
                  <div className="text-sm text-gray-500 group-hover:text-blue-600 mt-1">Your private tasks and projects</div>
                </div>
                {selectedTeamId === null && (
                  <div className="w-3 h-3 rounded-full bg-blue-500 shadow-sm"></div>
                )}
              </button>

              {/* Teams Section */}
              {teams.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-3 mx-6" />
                  <div className="px-6 py-2">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Your Teams ({teams.length})
                    </div>
                  </div>
                  
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="group flex items-center hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200"
                    >
                      <button
                        onClick={() => handleTeamSelect(team.id)}
                        className={`flex-1 px-6 py-4 text-left flex items-center gap-4 transition-all duration-200 ${
                          selectedTeamId === team.id ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-r-4 border-blue-500' : 'text-gray-700'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getTeamColor(team.id)} flex items-center justify-center text-white shadow-md font-bold text-lg`}>
                          {getTeamInitials(team.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate group-hover:text-blue-700">
                            {team.name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1 mt-1 group-hover:text-blue-600">
                            <Users className="w-3.5 h-3.5" />
                            <span>{team.member_count} member{team.member_count !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        {selectedTeamId === team.id && (
                          <div className="w-3 h-3 rounded-full bg-blue-500 shadow-sm"></div>
                        )}
                      </button>
                      
                      {onTeamSettings && (
                        <button
                          onClick={(e) => handleTeamSettings(team, e)}
                          className="opacity-0 group-hover:opacity-100 p-3 hover:bg-blue-100 rounded-xl transition-all duration-200 mr-3"
                          title="Team settings"
                        >
                          <Settings className="w-5 h-5 text-gray-500 hover:text-blue-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Actions Footer */}
            <div className="border-t border-gray-100 bg-gray-50 px-6 py-5 space-y-3">
              <button
                onClick={() => {
                  onCreateTeam();
                  setIsOpen(false);
                }}
                className="w-full px-5 py-3.5 text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold text-sm transform hover:-translate-y-0.5"
              >
                <Plus className="w-5 h-5" />
                <span>Create New Team</span>
              </button>
              
              <button
                onClick={() => {
                  onJoinTeam();
                  setIsOpen(false);
                }}
                className="w-full px-5 py-3.5 text-center border-2 border-blue-200 text-blue-700 rounded-xl hover:bg-blue-50 hover:border-blue-300 flex items-center justify-center gap-3 transition-all duration-200 font-semibold text-sm"
              >
                <Users className="w-5 h-5" />
                <span>Join Existing Team</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 